/*
  Availability check for combined-product-picker radios.

  For each .combined-product-picker on the page, we read the same
  parent_product_combination map the picker uses to navigate, HEAD-request
  each unique target short-handle at /products/{urlPrefix}{shortHandle}.js,
  and gray out any radio combo whose target product is draft/archived/deleted
  (Shopify returns 404 for those).

  Unlisted products still return 200 on that endpoint, so this catches
  draft/archived/deleted only. Unlisted needs a metafield-based signal if
  we want to gate it later.

  Behavior:
    - Radio+label get class `is-target-unavailable` (grayed via CSS in
      combined-product-picker.liquid).
    - Label gets `aria-disabled="true"` and title="Currently unavailable" for tooltip.
    - Click on an unavailable radio is preventDefault-ed so nothing navigates.
    - Availability map is cached in sessionStorage so repeat visits skip network.
    - We batch concurrent lookups at CONCURRENCY.
*/
(function () {
  var CONCURRENCY = 6;
  var CACHE_KEY = 'cctn_picker_target_avail_v1';
  var CACHE_TTL_MS = 5 * 60 * 1000; // 5 min — matches Shopify's page cache TTL

  var cache = (function () {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return {};
      var parsed = JSON.parse(raw);
      var now = Date.now();
      // Drop stale entries
      Object.keys(parsed).forEach(function (k) {
        if (!parsed[k] || (now - parsed[k].at) > CACHE_TTL_MS) delete parsed[k];
      });
      return parsed;
    } catch (e) { return {}; }
  })();
  function persist() {
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch (e) {}
  }

  function checkOne(url) {
    return fetch(url, { method: 'GET', credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.ok ? 'available' : 'unavailable'; })
      .catch(function () { return 'unknown'; });
  }

  function runQueue(queue, onProgress, onDone) {
    var active = 0;
    var results = {};
    var idx = 0;
    function next() {
      while (active < CONCURRENCY && idx < queue.length) {
        var job = queue[idx++];
        active++;
        (function (h) {
          checkOne(job.url).then(function (state) {
            results[h] = state;
            cache[h] = { state: state, at: Date.now() };
            active--;
            if (onProgress) onProgress(h, state, results);
            if (idx >= queue.length && active === 0) {
              persist();
              onDone(results);
            } else {
              next();
            }
          });
        })(job.handle);
      }
    }
    if (!queue.length) { onDone(results); return; }
    next();
  }

  function applyStateToPicker(picker, availabilityMap) {
    // Read the local lookup we embedded in the picker as a data attribute or via the
    // window-level variable. Rather than re-computing key→handle, we walk the radios
    // in each fieldset and infer their target by consulting the picker's own map,
    // which we ALSO embed alongside for consistency. Simpler: for each radio value,
    // we ask the picker for the resolved short-handle by reading data attrs we added.

    // Since we don't have per-radio target info in the DOM, we resolve target combos
    // for every possible full selection and disable radios that contribute ONLY to
    // fully-unavailable combos. For now, we do the simpler thing: disable a radio when
    // EVERY combo it participates in leads to an unavailable target. This is enough
    // because for CCTN, drafts are usually the whole product, not a partial combo.

    // For the initial ship, we do the simplest thing that helps: for each radio,
    // consult the currently-selected sibling values, resolve to a combo, check if
    // that combo's target is unavailable, and disable the radio.
    var localLookup = picker.__cctnLocalLookup;
    if (!localLookup) return;

    var fieldsets = Array.from(picker.querySelectorAll('fieldset[data-split-index]'));

    function stripSpace(s) { return String(s || '').replace(/\s+/g, ''); }

    fieldsets.forEach(function (fs, fsIndex) {
      Array.from(fs.querySelectorAll('input[type="radio"]')).forEach(function (input) {
        // Build a hypothetical selection: for other axes, use the currently checked value.
        var selection = fieldsets.map(function (other, otherIdx) {
          if (otherIdx === fsIndex) return input.value;
          var checked = other.querySelector('input:checked');
          return checked ? checked.value : '';
        });
        if (selection.some(function (v) { return v === ''; })) return; // incomplete
        var key = selection.map(stripSpace).join('|');
        var handle = localLookup[key];
        if (!handle) return;
        var avail = availabilityMap[handle] || (cache[handle] && cache[handle].state);
        var label = input.nextElementSibling;
        var unavailable = avail === 'unavailable';
        input.classList.toggle('is-target-unavailable', unavailable);
        if (label) {
          label.classList.toggle('is-target-unavailable', unavailable);
          if (unavailable) {
            label.setAttribute('aria-disabled', 'true');
            label.setAttribute('title', 'Currently unavailable');
          } else {
            label.removeAttribute('aria-disabled');
            label.removeAttribute('title');
          }
        }
      });
    });
  }

  function initPicker(picker) {
    if (picker.__cctnAvailInit) return;
    picker.__cctnAvailInit = true;

    // Read parent_product_combination that combined-product-picker.liquid renders
    // inline as `localLookup`. It's not exported on window; we pick it up via a
    // data attr set by that snippet (added below).
    var raw = picker.getAttribute('data-parent-lookup');
    var urlPrefix = picker.getAttribute('data-url-prefix') || '';
    var localeRoot = picker.getAttribute('data-locale-root') || '';
    if (!raw) return;
    var localLookup;
    try { localLookup = JSON.parse(raw); } catch (e) { return; }
    picker.__cctnLocalLookup = localLookup;

    var uniqueHandles = {};
    Object.keys(localLookup).forEach(function (k) {
      var h = localLookup[k];
      if (h) uniqueHandles[h] = true;
    });
    var handles = Object.keys(uniqueHandles);

    var jobs = [];
    var known = {};
    handles.forEach(function (h) {
      var cached = cache[h];
      if (cached && cached.state) { known[h] = cached.state; return; }
      jobs.push({ handle: h, url: localeRoot + '/products/' + urlPrefix + h + '.js' });
    });

    // Apply cached results immediately, then fill in the rest. We defer the
    // first apply to the next frame so the picker's own <script> block has
    // had a chance to run selectCurrentCombination() — otherwise no radio is
    // :checked yet and every combo evaluation bails on incomplete siblings.
    function deferredApply(map) {
      var run = function () { applyStateToPicker(picker, map); };
      if (window.requestAnimationFrame) {
        requestAnimationFrame(function () { requestAnimationFrame(run); });
      } else {
        setTimeout(run, 50);
      }
    }
    deferredApply(known);

    var running = Object.assign({}, known);
    runQueue(jobs,
      function (handle, state) {
        // Progressive: as each fetch lands, refresh the picker state so
        // the first draft/archived hit we discover gets grayed immediately,
        // without waiting for the whole queue to finish.
        running[handle] = state;
        deferredApply(running);
      },
      function (fresh) {
        deferredApply(Object.assign({}, known, fresh));
      }
    );

    // Intercept clicks on unavailable radios to prevent navigation.
    picker.addEventListener('click', function (event) {
      var target = event.target;
      if (target && target.matches && target.matches('input[type="radio"].is-target-unavailable')) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);

    // Re-apply state on any radio change (the currently-selected sibling values shift,
    // which changes which combos each other radio resolves to).
    picker.addEventListener('change', function () {
      var refresh = {};
      Object.keys(cache).forEach(function (k) { refresh[k] = cache[k].state; });
      applyStateToPicker(picker, refresh);
    });
  }

  function scan() {
    document.querySelectorAll('.combined-product-picker[data-parent-lookup]').forEach(initPicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
})();
